import { useBreakpoint } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import type { PopoverProps } from "@mui/material";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  Popover,
  Typography,
} from "@mui/material";
import { BottomSheet } from "@shared/components/BottomSheet";
import { useTheme } from "@shared/theme/useTheme";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";

const MenuHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border2};
`;

const HeaderTitleText = styled(Typography)`
  font-weight: 700;
`;

const MenuItemLabel = styled(Typography)`
  flex: 1;
  color: ${({ theme }) => theme.text.colors.brand};
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.text.fontSizes.m};
`;

const MenuListItemButton = styled(ListItemButton)`
  color: ${(p) => p.theme.text.colors.brand};
  &:hover {
    background-color: ${({ theme }) => theme.colors.background5};
  }

  /* dimming moves to MenuItemContent so endAdornment stays fully visible */
  &.Mui-disabled {
    opacity: 1;
  }
`;

const MenuItemContent = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  opacity: ${(p) => (p.$disabled ? 0.38 : 1)};
`;

const EndAdornmentSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 0.75rem;
`;

const defaultListItemButtonSx = {
  px: 2,
  py: 2,
};

type ActionSheetMenuHeaderConfig = {
  title: ReactNode;
  subtitle?: ReactNode;
};

type ActionSheetMenuProps = {
  header?: ActionSheetMenuHeaderConfig;
  children: ReactNode;
  minWidth?: string;
  id?: string;
};

export const ActionSheetMenu = (props: ActionSheetMenuProps) => {
  const { header, children, minWidth = "15rem", id } = props;
  const hasHeader = !!header;

  return (
    <Box id={id} role="menu" sx={{ background: "transparent", minWidth }}>
      {header && (
        <MenuHeader>
          <HeaderTitleText variant="body1" sx={{ m: 0, display: "block" }}>
            {header.title}
          </HeaderTitleText>
          {header.subtitle != null && header.subtitle !== "" && (
            <Typography variant="body2" sx={{ m: 0, display: "block" }}>
              {header.subtitle}
            </Typography>
          )}
        </MenuHeader>
      )}
      <List disablePadding sx={{ py: hasHeader ? 0 : 2 }}>
        {children}
      </List>
    </Box>
  );
};

const MENU_ICON_PX_MOBILE = 16;
const MENU_ICON_PX_WEB = 20;

const menuListItemIconSx = (
  theme: ReturnType<typeof useTheme>,
  iconFontSizePx: number,
) => ({
  color: theme.text.colors.brand,
  minWidth: "2rem",
  "& .MuiSvgIcon-root": { fontSize: iconFontSizePx },
});

type ActionSheetMenuItemProps = {
  icon: ReactNode;
  label: ReactNode;
  onClick: () => void | Promise<void>;
  endAdornment?: ReactNode;
  disabled?: boolean;
};

export const ActionSheetMenuItem = (props: ActionSheetMenuItemProps) => {
  const { icon, label, onClick, endAdornment, disabled = false } = props;
  const theme = useTheme();
  const isMobileMenu = useBreakpoint("laptop");
  const iconFontSizePx = isMobileMenu ? MENU_ICON_PX_MOBILE : MENU_ICON_PX_WEB;

  return (
    <MenuListItemButton
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      sx={defaultListItemButtonSx}
    >
      <MenuItemContent $disabled={disabled}>
        <ListItemIcon sx={menuListItemIconSx(theme, iconFontSizePx)}>
          {icon}
        </ListItemIcon>
        <MenuItemLabel variant="button">{label}</MenuItemLabel>
      </MenuItemContent>
      {endAdornment && <EndAdornmentSlot>{endAdornment}</EndAdornmentSlot>}
    </MenuListItemButton>
  );
};

type ActionSheetMenuItemWithDescriptionProps = Omit<
  ActionSheetMenuItemProps,
  "label"
> & {
  title: ReactNode;
  description: ReactNode;
};

export const ActionSheetMenuItemWithDescription = (
  props: ActionSheetMenuItemWithDescriptionProps,
) => {
  const { title, description, ...itemProps } = props;

  return (
    <ActionSheetMenuItem
      {...itemProps}
      label={
        <Box sx={{ textTransform: "none", letterSpacing: "normal" }}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ lineHeight: 1.3 }}
          >
            {description}
          </Typography>
        </Box>
      }
    />
  );
};

const StyledPopover = styled(Popover)`
  & .MuiPaper-root {
    min-width: 12rem;
    padding: 0;
    border-radius: 8px;
  }
`;

type ResponsiveActionSheetMenuRenderTriggerArgs = {
  /** Attach to the opening control (e.g. `IconButton`) for focus handling after close. */
  triggerRef: (instance: HTMLButtonElement | null) => void;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  /** `true` when the menu opens as a bottom sheet (e.g. laptop breakpoint down). */
  isSheet: boolean;
  /** Whether the menu surface is currently shown - wire to the trigger's `aria-expanded`. */
  open: boolean;
};

type ResponsiveActionSheetMenuProps = {
  renderTrigger: (
    args: ResponsiveActionSheetMenuRenderTriggerArgs,
  ) => ReactNode;
  /** Menu surface content; receives `closeMenu` to dismiss the sheet/popover after an action. */
  children: (closeMenu: () => void) => ReactNode;
  /** Fires when the user opens the menu (before the surface is shown). */
  onOpen?: () => void;
  anchorOrigin?: PopoverProps["anchorOrigin"];
  transformOrigin?: PopoverProps["transformOrigin"];
};

/**
 * Chooses {@link BottomSheet} vs {@link Popover} from the viewport and renders `children` inside it.
 */
export const ResponsiveActionSheetMenu = (
  props: ResponsiveActionSheetMenuProps,
) => {
  const {
    renderTrigger,
    children,
    onOpen,
    anchorOrigin = { vertical: "bottom", horizontal: "right" },
    transformOrigin = { vertical: "top", horizontal: "right" },
  } = props;

  const isSheet = useBreakpoint("laptop");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const setTriggerRef = useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node;
  }, []);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setOpen(false);
    setAnchorEl(null);
  }, [isSheet]);

  const blurTriggerSoon = useCallback(() => {
    requestAnimationFrame(() => {
      triggerRef.current?.blur();
    });
  }, []);

  const handleCloseMenu = () => {
    setOpen(false);
    setAnchorEl(null);
    blurTriggerSoon();
  };

  const handleTriggerClick = (event: MouseEvent<HTMLElement>) => {
    onOpen?.();
    if (!isSheet) {
      setAnchorEl(event.currentTarget);
    }
    setOpen(true);
  };

  return (
    <>
      {renderTrigger({
        triggerRef: setTriggerRef,
        onClick: handleTriggerClick,
        isSheet,
        open,
      })}

      {isSheet ? (
        <BottomSheet open={open} onClose={handleCloseMenu}>
          {children(handleCloseMenu)}
        </BottomSheet>
      ) : (
        <StyledPopover
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          disableRestoreFocus
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          {children(handleCloseMenu)}
        </StyledPopover>
      )}
    </>
  );
};
